import { apiSlice } from "./apiSlice";

const apiSliceWithTags = apiSlice.enhanceEndpoints({
  addTagTypes: ["Quizzes", "SubjectLevels"],
});

const quizApiSlice = apiSliceWithTags.injectEndpoints({
  endpoints: (builder) => ({
    getQuiz: builder.query({
      query: () => "/quizzes",
      providesTags: ["Quizzes"],
    }),
    getQuizzesPaginated: builder.query({
      query: ({ page = 1, per_page = 10, subject = null, level = null, type = null, sort_by = null, has_submissions = false, is_assigned = null } = {}) => {
        let url = `/quizzes?page=${page}&per_page=${per_page}`;
        if (subject) url += `&subject=${subject}`;
        if (level) url += `&level=${level}`;
        if (type) url += `&type=${type}`;
        if (sort_by) url += `&sort_by=${sort_by}`;
        if (has_submissions) url += `&has_submissions=true`;
        if (is_assigned !== null) url += `&is_assigned=${is_assigned}`;
        return url;
      },
      providesTags: ["Quizzes"],
    }),
    createQuiz: builder.mutation({
      query: (values) => ({
        url: "/quiz",
        method: "POST",
        body: { ...values },
      }),
      invalidatesTags: ["Quizzes"],
    }),
    getQuizResult: builder.query({
      query: ({ quiz_id }) => `/quiz/result?quiz_id=${quiz_id}`,
    }),
    submitQuiz: builder.mutation({
      query: (values) => ({
        url: "/quiz/submit-quiz",
        method: "POST",
        body: { ...values },
      }),
      invalidatesTags: ["Quizzes"],
    }),
    practiceWithAi: builder.mutation({
      query: (values) => ({
        url: "/quiz/practice/ai",
        method: "POST",
        body: { ...values },
      }),
      invalidatesTags: ["Quizzes"],
    }),
    generateStudentQuizAi: builder.mutation({
      query: ({ type, ...values }) => ({
        url: `/quiz/${type}/ai`,
        method: "POST",
        body: { ...values },
      }),
      invalidatesTags: ["Quizzes"],
    }),
    deleteQuiz: builder.mutation({
      query: ({ id }) => ({
        url: `/quiz/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Quizzes"],
    }),
    updateQuiz: builder.mutation({
      query: ({ id, ...values }) => ({
        url: `/quiz/${id}`,
        method: "PUT",
        body: { ...values },
      }),
      invalidatesTags: ["Quizzes"],
    }),
    getQuizById: builder.query({
      query: (arg) => {
        const id = typeof arg === 'object' ? arg.id : arg;
        const include_participants = typeof arg === 'object' ? arg.include_participants : true;
        let url = `/quiz/${id}`;
        if (include_participants === false) url += '?include_participants=false';
        return url;
      },
      providesTags: ["Quizzes"],
    }),
    addQuestionToQuiz: builder.mutation({
      query: ({ quizId, questionId }) => ({
        url: `/quiz/${quizId}/questions`,
        method: "POST",
        body: { question_id: questionId },
      }),
      invalidatesTags: ["Quizzes"],
    }),
    removeQuestionFromQuiz: builder.mutation({
      query: ({ quizId, questionId }) => ({
        url: `/quiz/${quizId}/questions/${questionId}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Quizzes"],
    }),
    getMyClasses: builder.query({
      query: () => "/classroom/all/mine",
    }),
    getMyStudentSubjects: builder.query({
      query: () => "/student/mine/subjects",
    }),
    getStudentClasses: builder.query({
      query: ({ studentId }) => `/student/${studentId}/classes`,
    }),
    getClassroomSubjects: builder.query({
      query: ({ classroom_id }) => `/classroom/${classroom_id}/subjects`,
    }),
    getClassroomStudents: builder.query({
      query: ({ classroom_id }) => `/classroom/${classroom_id}/students`,
    }),
    getClassroomAssessments: builder.query({
      query: ({ classroom_id }) => `/classroom/${classroom_id}/assessments`,
    }),
    getTopicsBySubjectAndLevel: builder.query({
      query: ({ subject, level }) => `/topics/${subject}/${level}`,
    }),
    getQuizSubmissions: builder.query({
      query: ({ quiz_id, page = 1, per_page = 20 }) =>
        `/quiz/${quiz_id}/submissions?page=${page}&per_page=${per_page}`,
      providesTags: ["Quizzes"],
    }),
    getSubmissionDetail: builder.query({
      query: ({ quiz_id, user_id }) => `/quiz/${quiz_id}/submissions/${user_id}`,
      providesTags: ["Quizzes"],
    }),
    checkAnswer: builder.mutation({
      query: (values) => ({
        url: "/quiz/check-answer",
        method: "POST",
        body: { ...values },
      }),
    }),
    getSchoolQuizzes: builder.query({
      query: () => "/quizzes/school",
      providesTags: ["Quizzes"],
    }),
    getMySubjectLevels: builder.query({
      query: () => "/student/subject-levels",
      providesTags: ["SubjectLevels"],
    }),
    initSubjectLevel: builder.mutation({
      query: (values) => ({
        url: "/student/subject-levels",
        method: "POST",
        body: { ...values },
      }),
      invalidatesTags: ["SubjectLevels"],
    }),
    startQuiz: builder.mutation({
      query: (id) => ({
        url: `/quiz/${id}/start`,
        method: "POST",
      }),
    }),
    getQuizParticipants: builder.query({
      query: (id) => `/quiz/${id}/participants`,
      providesTags: ["Quizzes"],
    }),
    addQuizParticipants: builder.mutation({
      query: ({ id, user_ids }) => ({
        url: `/quiz/${id}/participants`,
        method: "POST",
        body: { user_ids },
      }),
      invalidatesTags: ["Quizzes"],
    }),
    removeQuizParticipants: builder.mutation({
      query: ({ id, user_ids }) => ({
        url: `/quiz/${id}/participants`,
        method: "DELETE",
        body: { user_ids },
      }),
      invalidatesTags: ["Quizzes"],
    }),
    markAnswersViewed: builder.mutation({
      query: (id) => ({
        url: `/quiz/${id}/mark-answers-viewed`,
        method: "POST",
      }),
      invalidatesTags: ["Quizzes"],
    }),
  }),
});

export const {
  useGetQuizQuery,
  useGetQuizzesPaginatedQuery,
  useGetQuizResultQuery,
  useCreateQuizMutation,
  useSubmitQuizMutation,
  usePracticeWithAiMutation,
  useGenerateStudentQuizAiMutation,
  useDeleteQuizMutation,
  useUpdateQuizMutation,
  useGetQuizByIdQuery,
  useAddQuestionToQuizMutation,
  useRemoveQuestionFromQuizMutation,
  useGetMyClassesQuery,
  useGetMyStudentSubjectsQuery,
  useGetStudentClassesQuery,
  useGetClassroomSubjectsQuery,
  useGetClassroomStudentsQuery,
  useGetClassroomAssessmentsQuery,
  useGetTopicsBySubjectAndLevelQuery,
  useGetQuizSubmissionsQuery,
  useGetSubmissionDetailQuery,
  useCheckAnswerMutation,
  useGetSchoolQuizzesQuery,
  useGetMySubjectLevelsQuery,
  useInitSubjectLevelMutation,
  useStartQuizMutation,
  useGetQuizParticipantsQuery,
  useAddQuizParticipantsMutation,
  useRemoveQuizParticipantsMutation,
  useMarkAnswersViewedMutation,
} = quizApiSlice;
